'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@eliadmin/core/src/components/ui/select';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { changeLanguage } from '@eliadmin/core/src/server/api-client';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getFlagEmoji } from '@eliadmin/core/src/utils/locale-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

export function LanguageSelect() {
  const [isLoading, setIsLoading] = useState(false);
  const locale = useLocaleStore(s => s.currentLocale);
  const locales = useLocaleStore(s => s.supportedLocales);
  const [selectedLocale, setSelectedLocale] = useState(
    locales.find(localeIterator => localeIterator.locale === locale),
  );

  const handleLocaleChange = async (newLocale: string) => {
    const newRoute = `/${newLocale}`;
    setIsLoading(true);
    await changeLanguage({ locale: newLocale });
    window.location.replace(newRoute);
  };

  useEffect(() => {
    setSelectedLocale(locales.find(localeIterator => localeIterator.locale === locale));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  return (
    <div className={cn('w-auto', 'ea-language-select')}>
      <Select value={locale} onValueChange={handleLocaleChange}>
        <SelectTrigger
          aria-label={selectedLocale?.name?.trim() || 'Language'}
          className={cn(
            'my-1 h-9 border border-input bg-transparent',
            'ea-language-select__trigger',
          )}
          disabled={isLoading}
          innerTriggerClassName="h-auto"
        >
          {isLoading ? (
            <Spinner className={cn('w-8', 'ea-language-select__spinner')} />
          ) : selectedLocale ? (
            <Image
              src={selectedLocale?.image?.url || '/images/placeholder.png'}
              alt={resolveImageAlt(selectedLocale?.image, selectedLocale?.locale || '')}
              className={cn('min-h-5 pr-2', 'ea-language-select__flag')}
              width={32}
              height={30}
            />
          ) : (
            <span className={cn('px-2 text-xl', 'ea-language-select__flag')}>
              {getFlagEmoji(locale)}
            </span>
          )}
        </SelectTrigger>
        <SelectContent className={cn('', 'ea-language-select__content')}>
          {locales.map(locale => (
            <SelectItem
              key={`${locale.name}-${locale.locale}`}
              value={locale.locale}
              className={cn('', 'ea-language-select__item')}
            >
              {locale.image ? (
                <div className="flex">
                  <Image
                    src={locale.image.url ?? '/images/placeholder.png'}
                    alt={resolveImageAlt(locale?.image, locale?.locale)}
                    className={cn('pr-2', 'ea-language-select__flag')}
                    width={25}
                    height={20}
                  />
                  {locale.name}
                </div>
              ) : (
                <>
                  <span className={cn('pr-2', 'ea-language-select__flag')}>
                    {getFlagEmoji(locale.locale)}
                  </span>
                  {locale.name}
                </>
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
